import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CreateWorkspaceParams,
  FetchWorkspaceResponse,
  UpdateWorkspaceParams,
  WorkspaceWorkspaceApiKeys,
} from '@zond/api-interfaces';
import { AxiosError } from 'axios';
import { client } from '../../../client';

export const useUpdateWorkspace = (onSuccess?: () => void, onError?: () => void) => {
  return useMutation<
    FetchWorkspaceResponse,
    AxiosError,
    { workspaceId: number; data: Partial<UpdateWorkspaceParams> }
  >({
    mutationFn: ({ workspaceId, data }) =>
      client.patch(`/workspaces/${workspaceId}`, data).then((res) => res.data),
    onSuccess,
    onError,
  });
};

export const useGetWorkspace = (id?: number) => {
  return useQuery<FetchWorkspaceResponse>({
    queryKey: ['workspace', id],
    queryFn: () => client.get(`/workspaces/${id}`).then((res) => res.data),
    enabled: !!id,
  });
};

export const useGetWorkspaces = () => {
  return useQuery({
    queryKey: ['workspaces'],
    queryFn: () => client.get('/workspaces').then((res) => res.data),
  });
};

export const useCreateWorkspace = (onSuccess?: () => void, onError?: () => void) => {
  return useMutation<FetchWorkspaceResponse, AxiosError, CreateWorkspaceParams>({
    mutationFn: (data: CreateWorkspaceParams) => client.post('/workspaces', data).then((res) => res.data),
    onSuccess,
    onError,
  });
};

export const useUpdateWorkspaceAvatar = (onSuccess?: () => void, onError?: () => void) => {
  return useMutation<FetchWorkspaceResponse, AxiosError, File>({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return client.put('/workspaces/avatar', formData).then((res) => res.data);
    },
    onSuccess,
    onError,
  });
};

export const useGetWorkspaceApiKeys = () => {
  return useQuery<WorkspaceWorkspaceApiKeys[]>({
    queryKey: ['workspace-api-keys'],
    queryFn: () => client.get('/workspace-api-keys').then((res) => res.data),
  });
};

export const useCreateWorkspaceApiKey = (onSuccess?: () => void, onError?: () => void) => {
  return useMutation<{ key: string; id: number }, AxiosError, string>({
    mutationFn: (name: string) => client.post('/workspace-api-keys', { name }).then((res) => res.data),
    onSuccess,
    onError,
  });
};

export const useDeleteWorkspaceApiKey = (onSuccess?: () => void, onError?: () => void) => {
  return useMutation<void, AxiosError, number>({
    mutationFn: (workspaceApiKeyId: number) =>
      client.delete(`/workspace-api-keys/${workspaceApiKeyId}`).then((res) => res.data),
    onSuccess,
    onError,
  });
};

export const useGetWorkspaceMembers = (workspaceId?: number) => {
  return useQuery({
    queryKey: ['workspace-members', workspaceId],
    queryFn: () => client.get(`/workspaces/${workspaceId}/members`).then((res) => res.data),
    enabled: !!workspaceId,
  });
};

export const useUpdateMember = (onSuccess?: () => void, onError?: () => void) => {
  return useMutation<any, AxiosError, { memberId: number; data: { role: string } }>({
    mutationFn: ({ memberId, data }) =>
      client.patch(`/workspace-members/${memberId}`, data).then((res) => res.data),
    onSuccess,
    onError,
  });
};

export const useDeleteMember = (onSuccess?: () => void, onError?: () => void) => {
  return useMutation<void, AxiosError, number>({
    mutationFn: (memberId: number) => client.delete(`/workspace-members/${memberId}`).then((res) => res.data),
    onSuccess,
    onError,
  });
};

export const useInviteMember = (onSuccess?: () => void, onError?: () => void) => {
  return useMutation<any, AxiosError, { workspaceId: number; data: { email: string; role: string } }>({
    mutationFn: ({ workspaceId, data }) =>
      client.post(`/workspaces/${workspaceId}/members`, data).then((res) => res.data),
    onSuccess,
    onError,
  });
};
